import type { Common } from '../common.js';
import type { Audit, QueryParams as AuditQueryParams } from '../models/flow/audit.js';
import {
  type CreateValues,
  type FlowFindAllQueryParams,
  type FlowWithCreatedBy,
  type FlowWithDefinitions,
  type FlowWithRunCount,
  type UpdateValues,
} from '../models/flow/flow.js';
import { type SubflowDefinitionWithFlow } from '../models/flow/flowDefinition.js';
import { type FlowVersionTarget } from '../models/flow/flowExecution.js';

export class API {
  common: Common;
  baseEndpoint = '/flow/flows';

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(queryParams?: FlowFindAllQueryParams): Promise<FlowWithRunCount[]> {
    const url = this.common.constructPath(this.baseEndpoint, queryParams);
    return await this.common.getJSON(url);
  }

  async findOneById(flowId: string): Promise<FlowWithDefinitions> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${flowId}`);
    return await this.common.getJSON(url);
  }

  async create(values: CreateValues): Promise<FlowWithCreatedBy> {
    const url = this.common.constructPath(this.baseEndpoint);
    const flow = await this.common.postJSON(url, values);
    return flow;
  }

  async update(flowId: string, values: UpdateValues): Promise<FlowWithCreatedBy> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${flowId}`);
    return await this.common.patchJSON(url, values);
  }

  async getOmniInsightsUrl(args: { flowId: string; timeZone: string }): Promise<{ src: string }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${args.flowId}/omni-insights`);
    return await this.common.postJSON(url, { timeZone: args.timeZone });
  }

  async findAuditTrail(flowId: string, query?: AuditQueryParams): Promise<Audit[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${flowId}/audit-trail`, query);
    return await this.common.getJSON(url);
  }

  async findSubFlowVersion(args: {
    flowId: string;
    target?: FlowVersionTarget;
    flowDefinitionId?: string;
  }): Promise<SubflowDefinitionWithFlow> {
    const { flowId, ...rest } = args;
    const url = this.common.constructPath(`${this.baseEndpoint}/${flowId}/subflow-version`, rest);
    return await this.common.getJSON(url);
  }
}
